import React, { useEffect, useRef, useState } from 'react';
import Socket from '../../../connection/socket';

interface DebugBotPresenterProps {
	socket: Socket | null;
}

type BotEvent = {
	skill?: string;
	status?: string;
	error?: string;
	timestamp?: string;
	session?: string;
};

type SequenceStep = {
	id: string;
	label: string;
	skill: string;
	params: Record<string, unknown>;
};

export default function DebugBotPresenter({ socket }: DebugBotPresenterProps) {
	const [log, setLog] = useState<BotEvent[]>([]);
	const [presenterCount, setPresenterCount] = useState<number>(1);
	const [layoutNumber, setLayoutNumber] = useState<number>(1);
	const [layoutLetter, setLayoutLetter] = useState<string>('A');
	const [feedIndex, setFeedIndex] = useState<number>(0);
	const [canvasPosition, setCanvasPosition] = useState<number>(0);
	const [sequence, setSequence] = useState<SequenceStep[]>([]);
	const [isRunningSequence, setIsRunningSequence] = useState<boolean>(false);

	const pendingStepResolveRef = useRef<((entry: BotEvent) => void) | null>(null);

	useEffect(() => {
		socket?.addListener('bot-presenter-event', (msg: { message: BotEvent, session: string }) => {
			setLog((prev) => [{ ...msg.message, session: msg.session }, ...prev].slice(0, 20));

			// If a sequence step is waiting on a response, this is it — resolve it
			// via this same listener instead of adding a second one for the socket
			// (its removeListener drops by event name, not by specific callback).
			if (pendingStepResolveRef.current) {
				const resolve = pendingStepResolveRef.current;
				pendingStepResolveRef.current = null;
				resolve(msg.message);
			}
		});

		return () => socket?.removeListener('bot-presenter-event');
	}, [socket]);

	const sendCommand = (skill: string, params: Record<string, unknown> = {}) => {
		socket?.sendMessage('bot-presenter-command', { skill, ...params });
	};

	// Sends a command and resolves once the corresponding bot-presenter-event
	// comes back — used by the sequence runner so each step's effects (e.g. a
	// mode switch) are fully settled before the next step fires.
	const sendCommandAndWait = (skill: string, params: Record<string, unknown> = {}): Promise<BotEvent> => (
		new Promise((resolve) => {
			pendingStepResolveRef.current = resolve;
			sendCommand(skill, params);
		})
	);

	const addToSequence = (label: string, skill: string, params: Record<string, unknown> = {}) => {
		setSequence((prev) => [...prev, { id: `${Date.now()}-${Math.random()}`, label, skill, params }]);
	};

	const removeFromSequence = (id: string) => {
		setSequence((prev) => prev.filter((step) => step.id !== id));
	};

	const clearSequence = () => setSequence([]);

	const runSequence = async () => {
		setIsRunningSequence(true);
		for (const step of sequence) {
			// eslint-disable-next-line no-await-in-loop
			await sendCommandAndWait(step.skill, step.params);
		}
		setIsRunningSequence(false);
	};

	const buttonStyle: React.CSSProperties = {
		padding: '8px 16px',
		borderRadius: '12px',
	};

	const addButtonStyle: React.CSSProperties = {
		padding: '4px 10px',
		borderRadius: '12px',
	};

	return (
		<div className="section settings">
			<h5>Bot Presenter</h5>
			<div className="advanced-settings-container">
				<div className="align-items-center justify-content-space-between">
					<button style={addButtonStyle} onClick={() => addToSequence('Open Feeds Drawer', 'open_feeds_drawer')}>+</button>
					<button style={buttonStyle} onClick={() => sendCommand('open_feeds_drawer')}>Open Feeds Drawer</button>
				</div>
				<div className="align-items-center justify-content-space-between">
					<button style={addButtonStyle} onClick={() => addToSequence(`Invite Presenter Users (${presenterCount})`, 'invite_presenters', { count: presenterCount })}>+</button>
					<button style={buttonStyle} onClick={() => sendCommand('invite_presenters', { count: presenterCount })}>Invite Presenter Users</button>
					<input
						type="number"
						min={1}
						max={20}
						value={presenterCount}
						onChange={(e) => setPresenterCount(
							Math.min(20, Math.max(1, Number(e.target.value) || 1)),
						)}
						style={{ width: '80px' }}
					/>
				</div>
				<div className="align-items-center justify-content-space-between">
					<button style={addButtonStyle} onClick={() => addToSequence('Switch to Showbuilder', 'switch_to_showbuilder')}>+</button>
					<button style={buttonStyle} onClick={() => sendCommand('switch_to_showbuilder')}>Switch to Showbuilder</button>
				</div>
				<div className="align-items-center justify-content-space-between">
					<button style={addButtonStyle} onClick={() => addToSequence('Switch to Streaming', 'switch_to_streaming')}>+</button>
					<button style={buttonStyle} onClick={() => sendCommand('switch_to_streaming')}>Switch to Streaming</button>
				</div>
				<div className="align-items-center justify-content-space-between">
					<button style={addButtonStyle} onClick={() => addToSequence('Switch to Recording', 'switch_to_recording')}>+</button>
					<button style={buttonStyle} onClick={() => sendCommand('switch_to_recording')}>Switch to Recording</button>
				</div>
				<div className="align-items-center justify-content-space-between">
					<button style={addButtonStyle} onClick={() => addToSequence('Add Shot', 'add_shot')}>+</button>
					<button style={buttonStyle} onClick={() => sendCommand('add_shot')}>Add Shot</button>
				</div>
				<div className="align-items-center justify-content-space-between">
					<button style={addButtonStyle} onClick={() => addToSequence('Mute Self Audio', 'mute_self_audio')}>+</button>
					<button style={buttonStyle} onClick={() => sendCommand('mute_self_audio')}>Mute Self Audio</button>
				</div>
				<div className="align-items-center justify-content-space-between">
					<button style={addButtonStyle} onClick={() => addToSequence('Mute Self Video', 'mute_self_video')}>+</button>
					<button style={buttonStyle} onClick={() => sendCommand('mute_self_video')}>Mute Self Video</button>
				</div>
				<div className="align-items-center justify-content-space-between">
					<button style={addButtonStyle} onClick={() => addToSequence(`Set Layout (${layoutNumber}${layoutLetter})`, 'set_layout', { number: layoutNumber, letter: layoutLetter })}>+</button>
					<button style={buttonStyle} onClick={() => sendCommand('set_layout', { number: layoutNumber, letter: layoutLetter })}>Set Layout</button>
					<div className='flex'>
					<input
						type="number"
						min={1}
						max={8}
						value={layoutNumber}
						onChange={(e) => setLayoutNumber(Math.max(1, Number(e.target.value) || 1))}
						style={{ width: '80px', marginRight: '16px' }}
					/>
					<input
						type="text"
						maxLength={1}
						value={layoutLetter}
						onChange={(e) => setLayoutLetter(e.target.value.toUpperCase())}
						style={{ width: '80px' }}
					/>
					</div>
				</div>
				<div className="align-items-center justify-content-space-between">
					<button style={addButtonStyle} onClick={() => addToSequence(`Drag Feed ${feedIndex} to Slot ${canvasPosition}`, 'drag_feed_to_canvas', { feedIndex, position: canvasPosition })}>+</button>
					<button style={buttonStyle} onClick={() => sendCommand('drag_feed_to_canvas', { feedIndex, position: canvasPosition })}>Drag Feed to Canvas</button>
					<span>Feed</span>
					<input
						type="number"
						min={0}
						max={19}
						value={feedIndex}
						onChange={(e) => setFeedIndex(Math.max(0, Number(e.target.value) || 0))}
						style={{ width: '80px' }}
					/>
					<span>Slot</span>
					<input
						type="number"
						min={0}
						max={7}
						value={canvasPosition}
						onChange={(e) => setCanvasPosition(Math.min(7, Math.max(0, Number(e.target.value) || 0)))}
						style={{ width: '80px' }}
					/>
				</div>
			</div>
			<h5>Sequence</h5>
			<div className="advanced-settings-container">
				{sequence.length === 0 && <span>No steps queued — use the + buttons above.</span>}
				{sequence.map((step) => (
					<div key={step.id} className="align-items-center justify-content-space-between">
						<button
							style={buttonStyle}
							disabled={isRunningSequence}
							onClick={() => removeFromSequence(step.id)}
						>
							{step.label}
						</button>
					</div>
				))}
				{sequence.length > 0 && (
					<div className="align-items-center justify-content-space-between">
						<button style={buttonStyle} disabled={isRunningSequence} onClick={runSequence}>
							{isRunningSequence ? 'Running…' : '▶ Run Sequence'}
						</button>
						<button style={buttonStyle} disabled={isRunningSequence} onClick={clearSequence}>
							✕ Clear
						</button>
					</div>
				)}
			</div>
			<h5>Action Log</h5>
			<div className="advanced-settings-container">
				{log.length === 0 && <span>No events yet.</span>}
				{log.map((entry, i) => (
					<div key={i} className="vertical-padding-small">
						<span>{entry.timestamp} — {entry.skill} — {entry.status}{entry.error ? `: ${entry.error}` : ''}</span>
					</div>
				))}
			</div>
		</div>
	);
}
